import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { ARQualificationData } from './types';
import type { SelectOption } from '../../../components';
import type { DepartmentsAndEntryYear } from './useGetARVisaApplicationList';

const GET_AR_PAGINATED_QUALIFICATIONS = gql`
  query (
    $filter: OfferedApplicationFilter!
    $sort: OfferDetailSorter
    $paginator: PaginatorOptions
  ) {
    qualificationApplicationList(
      filter: $filter
      sort: $sort
      paginator: $paginator
    ) {
      data {
        applicationId
        applicationNo
        applicantName
        programmeType
        programmeCode
        entryYear
        applicantNationality
        status
        displayStatus
      }
      paginator {
        count
        total_pages
      }
    }
  }
`;

const GET_META_LIST = gql`
  query {
    programmeChoiceMetadata {
      departments {
        value
        label
      }
      entryYears
      countryList {
        value
        label
      }
    }
  }
`;
export type Paginator = {
  page: number;
  limit: number;
};

export type ARPaginatedQualification = {
  data: ARQualificationData[];
  totalPage: number;
  count: number;
};

export type GetARPaginatedQualifications = {
  arPaginatedQualifications: ARPaginatedQualification;
  countryList: SelectOption[];
  departmentsAndEntryYear: DepartmentsAndEntryYear;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export const useGetARQualifications = (
  filter: any,
  sort: any,
  paginator: Paginator,
): GetARPaginatedQualifications => {
  const {
    data: ARQualifications,
    loading: ARQualificationsLoading,
    error: ARQualificationsError,
    refetch,
  } = useQuery(GET_AR_PAGINATED_QUALIFICATIONS, {
    variables: { filter, sort, paginator },
    fetchPolicy: 'network-only',
  });

  const {
    data: metaData,
    loading: metaDataLoading,
    error: metaDataError,
  } = useQuery(GET_META_LIST, {
    fetchPolicy: 'network-only',
  });

  const { data: paginatedData, paginator: returnedPaginator } =
    ARQualifications?.qualificationApplicationList ?? {};

  const arPaginatedQualifications = {
    data: paginatedData ?? [],
    totalPage: returnedPaginator?.total_pages,
    count: returnedPaginator?.count,
  };

  const countryList: SelectOption[] =
    metaData?.programmeChoiceMetadata?.countryList ?? [];

  const departmentsAndEntryYear: DepartmentsAndEntryYear = {
    departments: metaData?.programmeChoiceMetadata?.departments ?? [],
    entryYears: metaData?.programmeChoiceMetadata?.entryYears ?? [],
  };

  return {
    arPaginatedQualifications,
    countryList,
    departmentsAndEntryYear,
    loading: ARQualificationsLoading || metaDataLoading,
    error: ARQualificationsError ?? metaDataError,
    refetch,
  };
};
